// src/api/kardexHerramienta.js
import { apiFetch } from './index.js';

// NOTA: Los endpoints de Kardex están dentro de HerramientaController
// Ruta base: /api/Herramienta/kardex
const API_BASE = '/api/Herramienta/kardex';

/**
 * Obtener todos los movimientos de Kardex
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardex = async () => {
  try {
    const response = await apiFetch(API_BASE);
    return { success: true, data: response };
  } catch (error) {
    console.error('Error al obtener Kardex:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener un movimiento de Kardex por ID
 * @param {number} id - ID del movimiento
 * @returns {Promise} Datos del movimiento
 */
export const obtenerKardexPorId = async (id) => {
  try {
    const response = await apiFetch(`${API_BASE}/${id}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex ID ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Crear un nuevo movimiento de Kardex
 * @param {Object} kardexData - Datos del movimiento
 * @returns {Promise} Movimiento creado
 */
export const crearKardex = async (kardexData) => {
  try {
    const response = await apiFetch(API_BASE, {
      method: 'POST',
      body: JSON.stringify(kardexData)
    });
    return { success: true, data: response };
  } catch (error) {
    console.error('Error al crear Kardex:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Eliminar un movimiento de Kardex
 * @param {number} id - ID del movimiento
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarKardex = async (id) => {
  try {
    const response = await apiFetch(`${API_BASE}/${id}`, {
      method: 'DELETE'
    });
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al eliminar Kardex ID ${id}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por ID de herramienta
 * @param {number} idHerramienta - ID de la herramienta
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorHerramienta = async (idHerramienta) => {
  try {
    const response = await apiFetch(`${API_BASE}/herramienta/${idHerramienta}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por herramienta ${idHerramienta}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por tipo de movimiento
 * @param {string} tipo - Tipo de movimiento ('entrada', 'salida', 'ajuste', 'devolucion')
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorTipo = async (tipo) => {
  try {
    const response = await apiFetch(`${API_BASE}/tipo/${tipo}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por tipo ${tipo}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por sede
 * @param {number} idSede - ID de la sede
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorSede = async (idSede) => {
  try {
    const response = await apiFetch(`${API_BASE}/sede/${idSede}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por sede ${idSede}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por categoría
 * @param {number} idCategoria - ID de la categoría
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorCategoria = async (idCategoria) => {
  try {
    const response = await apiFetch(`${API_BASE}/categoria/${idCategoria}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por categoría ${idCategoria}:`, error);
    return { success: false, error: error.message };
  }
};

// =====================================================
// NUEVAS FUNCIONES PARA LAS NUEVAS PROPIEDADES
// =====================================================

/**
 * Obtener movimientos de Kardex por estado del equipo
 * @param {string} estadoEquipo - Estado del equipo (venta, repuesto, abandonado_por_cliente, para_reparacion)
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorEstadoEquipo = async (estadoEquipo) => {
  try {
    const response = await apiFetch(`${API_BASE}/estado/${estadoEquipo}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por estado ${estadoEquipo}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por teléfono del cliente
 * @param {string} telefono - Teléfono del cliente
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorTelefonoCliente = async (telefono) => {
  try {
    const response = await apiFetch(`${API_BASE}/telefono/${telefono}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por teléfono ${telefono}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por rango de fecha de ingreso manual
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorFechaIngresoManual = async (fechaInicio, fechaFin) => {
  try {
    const response = await apiFetch(`${API_BASE}/fechaIngreso?inicio=${fechaInicio}&fin=${fechaFin}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por fecha de ingreso:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener movimientos de Kardex por tipo de ingreso
 * @param {string} tipoIngreso - Tipo de ingreso (reparacion, venta, garantia, etc)
 * @returns {Promise} Lista de movimientos
 */
export const obtenerKardexPorTipoIngreso = async (tipoIngreso) => {
  try {
    const response = await apiFetch(`${API_BASE}/tipoIngreso/${tipoIngreso}`);
    return { success: true, data: response };
  } catch (error) {
    console.error(`Error al obtener Kardex por tipo de ingreso ${tipoIngreso}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener resumen de Kardex por herramienta
 * @param {number} idHerramienta - ID de la herramienta
 * @returns {Promise} Resumen con total de entradas, salidas y stock actual
 */
export const obtenerResumenKardex = async (idHerramienta) => {
  try {
    const response = await obtenerKardexPorHerramienta(idHerramienta);
    if (!response.success) return response;

    const movimientos = response.data || [];
    const totalEntradas = movimientos
      .filter(m => m.tipoMovimiento?.toLowerCase() === 'entrada')
      .reduce((sum, m) => sum + m.cantidad, 0);

    const totalSalidas = movimientos
      .filter(m => m.tipoMovimiento?.toLowerCase() === 'salida')
      .reduce((sum, m) => sum + m.cantidad, 0);

    const stockActual = movimientos.length > 0
      ? movimientos[movimientos.length - 1]?.stockNuevo || 0
      : 0;

    return {
      success: true,
      data: {
        totalEntradas,
        totalSalidas,
        stockActual,
        movimientos
      }
    };
  } catch (error) {
    console.error(`Error al obtener resumen Kardex:`, error);
    return { success: false, error: error.message };
  }
};